import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const inputClass =
  "p-3 rounded-lg bg-bg-soft border border-border w-full text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent transition";

function IngestionPage() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

  const [mode, setMode] = useState("local");
  const [file, setFile] = useState(null);
  const [s3Key, setS3Key] = useState("");
  const [vectorDB, setVectorDB] = useState("qdrant");
  const [loading, setLoading] = useState(false);

  const [openaiKey, setOpenaiKey] = useState("");
  const [cohereKey, setCohereKey] = useState("");

  const [awsAccessKey, setAwsAccessKey] = useState("");
  const [awsSecretKey, setAwsSecretKey] = useState("");
  const [awsRegion, setAwsRegion] = useState("");
  const [awsBucketName, setAwsBucketName] = useState("");
  const [txtFilesBucket, setTxtFilesBucket] = useState("");
  const [collectionName, setCollectionName] = useState("");

  const [mongoURI, setMongoURI] = useState("");
  const [mongoDBName, setMongoDBName] = useState("");

  const [weaviateAPIKey, setWeaviateAPIKey] = useState("");
  const [weaviateURL, setWeaviateURL] = useState("");
  const [qdrantAPIKey, setQdrantAPIKey] = useState("");
  const [qdrantURL, setQdrantURL] = useState("");
  const [chromaHost, setChromaHost] = useState("");
  const [chromaAPIKey, setChromaAPIKey] = useState("");

  const handleUpload = async () => {
    if (!vectorDB) {
      alert("⚠️ Please select a vector database.");
      return;
    }

    try {
      let response;
      setLoading(true);

      if (mode === "local") {
        if (!file) {
          setLoading(false);
          alert("⚠️ Please select a file to upload.");
          return;
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("vector_db", vectorDB);
        formData.append("openai_api_key", openaiKey);

        if (vectorDB === "weaviate") {
          formData.append("cohere_api_key", cohereKey);
          formData.append("weaviate_api_key", weaviateAPIKey);
          formData.append("weaviate_url", weaviateURL);
        } else if (vectorDB === "qdrant") {
          formData.append("qdrant_api_key", qdrantAPIKey);
          formData.append("qdrant_url", qdrantURL);
        } else if (vectorDB === "chroma") {
          formData.append("chroma_host", chromaHost);
          formData.append("chroma_api_key", chromaAPIKey);
        }

        if (mongoURI && mongoDBName) {
          formData.append("mongo_uri", mongoURI);
          formData.append("mongo_db_name", mongoDBName);
        }

        response = await fetch(`${API_BASE_URL}/ingest-local-document`, {
          method: "POST",
          body: formData,
        });
      } else if (mode === "s3") {
        if (!s3Key || !awsAccessKey || !awsSecretKey || !awsBucketName) {
          setLoading(false);
          alert("⚠️ Please fill in all required AWS fields.");
          return;
        }

        const payload = {
          s3_key: s3Key,
          vector_db: vectorDB,
          aws_config: {
            access_key_id: awsAccessKey,
            secret_access_key: awsSecretKey,
            region_name: awsRegion,
            bucket_name: awsBucketName,
            txt_files_bucket_name: awsBucketName,
          },
          openai_config: { api_key: openaiKey },
        };

        if (vectorDB === "weaviate") {
          payload.cohere_config = { api_key: cohereKey };
          payload.weaviate_config = { api_key: weaviateAPIKey, url: weaviateURL };
        } else if (vectorDB === "qdrant") {
          payload.qdrant_config = { api_key: qdrantAPIKey, url: qdrantURL };
        } else if (vectorDB === "chroma") {
          payload.chroma_config = { host: chromaHost, api_key: chromaAPIKey };
        }

        if (mongoURI && mongoDBName) {
          payload.mongo_config = { uri: mongoURI, db_name: mongoDBName };
        }

        response = await fetch(`${API_BASE_URL}/ingest-S3-document`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      const text = await response.text();
      setLoading(false);

      if (!response.ok) {
        alert(`❌ Ingestion failed: ${text}`);
      } else {
        alert("✅ Ingestion successful!");
      }
    } catch (err) {
      console.error("❌ Ingestion Error:", err);
      setLoading(false);
      alert("⚠️ Ingestion failed. Check console for details.");
    }
  };

  return (
    <div className="bg-bg min-h-screen text-text-primary relative">
      <Navbar />

      <div className="py-12 px-6 max-w-3xl mx-auto">
        <h2 className="text-3xl font-bold mb-8 text-center text-text-primary">
          Ingest Documents
        </h2>

        <div className="bg-white border border-border p-6 rounded-lg shadow-sm">

          {/* MODE SELECTION */}
          <div className="flex justify-center gap-4 mb-6">
            <button
              onClick={() => setMode("local")}
              className={`px-4 py-2 rounded-md font-semibold transition border ${
                mode === "local"
                  ? "bg-primary text-white border-primary"
                  : "bg-bg-soft text-text-primary border-border hover:border-border-strong"
              }`}
            >
              Local Upload
            </button>
            <button
              onClick={() => setMode("s3")}
              className={`px-4 py-2 rounded-md font-semibold transition border ${
                mode === "s3"
                  ? "bg-primary text-white border-primary"
                  : "bg-bg-soft text-text-primary border-border hover:border-border-strong"
              }`}
            >
              S3 Ingestion
            </button>
          </div>

          {/* VECTOR DB SELECTION */}
          <select
            value={vectorDB}
            onChange={(e) => setVectorDB(e.target.value)}
            className={`${inputClass} mb-6`}
          >
            <option value="">Select Vector Database</option>
            <option value="weaviate">Weaviate</option>
            <option value="qdrant">Qdrant</option>
            <option value="chroma">Chroma</option>
          </select>

          {/* LOCAL MODE */}
          {mode === "local" && (
            <div className="space-y-3">
              <input
                type="file"
                accept=".pdf,.docx,.csv,.txt"
                onChange={(e) => setFile(e.target.files[0])}
                className={inputClass}
              />
              <input type="text" placeholder="OpenAI API Key" value={openaiKey} onChange={(e) => setOpenaiKey(e.target.value)} className={inputClass} />
              <input type="text" placeholder="Collection Name" value={collectionName} onChange={(e) => setCollectionName(e.target.value)} className={inputClass} />

              {vectorDB === "weaviate" && (
                <>
                  <input type="text" placeholder="Cohere API Key" value={cohereKey} onChange={(e) => setCohereKey(e.target.value)} className={inputClass} />
                  <input type="text" placeholder="Weaviate API Key" value={weaviateAPIKey} onChange={(e) => setWeaviateAPIKey(e.target.value)} className={inputClass} />
                  <input type="text" placeholder="Weaviate URL" value={weaviateURL} onChange={(e) => setWeaviateURL(e.target.value)} className={inputClass} />
                </>
              )}

              {vectorDB === "qdrant" && (
                <>
                  <input type="text" placeholder="Qdrant API Key" value={qdrantAPIKey} onChange={(e) => setQdrantAPIKey(e.target.value)} className={inputClass} />
                  <input type="text" placeholder="Qdrant URL" value={qdrantURL} onChange={(e) => setQdrantURL(e.target.value)} className={inputClass} />
                </>
              )}

              {vectorDB === "chroma" && (
                <>
                  <input type="text" placeholder="Chroma Host" value={chromaHost} onChange={(e) => setChromaHost(e.target.value)} className={inputClass} />
                  <input type="text" placeholder="Chroma API Key" value={chromaAPIKey} onChange={(e) => setChromaAPIKey(e.target.value)} className={inputClass} />
                </>
              )}

              <input type="text" placeholder="MongoDB URI (optional)" value={mongoURI} onChange={(e) => setMongoURI(e.target.value)} className={inputClass} />
              <input type="text" placeholder="MongoDB Database Name (optional)" value={mongoDBName} onChange={(e) => setMongoDBName(e.target.value)} className={inputClass} />
            </div>
          )}

          {/* S3 MODE */}
          {mode === "s3" && (
            <div className="space-y-3">
              <input type="text" placeholder="Enter S3 Key (e.g., region-folder/topic-folder/document.pdf)" value={s3Key} onChange={(e) => setS3Key(e.target.value)} className={inputClass} />
              <input type="text" placeholder="AWS Access Key" value={awsAccessKey} onChange={(e) => setAwsAccessKey(e.target.value)} className={inputClass} />
              <input type="text" placeholder="AWS Secret Key" value={awsSecretKey} onChange={(e) => setAwsSecretKey(e.target.value)} className={inputClass} />
              <input type="text" placeholder="AWS Region Name (e.g., us-east-1)" value={awsRegion} onChange={(e) => setAwsRegion(e.target.value)} className={inputClass} />
              <input type="text" placeholder="S3 Bucket Name" value={awsBucketName} onChange={(e) => setAwsBucketName(e.target.value)} className={inputClass} />
              <input type="text" placeholder="Text File Bucket Name" value={txtFilesBucket} onChange={(e) => setTxtFilesBucket(e.target.value)} className={inputClass} />
              <input type="text" placeholder="Collection Name" value={collectionName} onChange={(e) => setCollectionName(e.target.value)} className={inputClass} />
              <input type="text" placeholder="OpenAI API Key" value={openaiKey} onChange={(e) => setOpenaiKey(e.target.value)} className={inputClass} />

              {vectorDB === "weaviate" && (
                <input type="text" placeholder="Cohere API Key" value={cohereKey} onChange={(e) => setCohereKey(e.target.value)} className={inputClass} />
              )}

              <input type="text" placeholder="MongoDB URI (optional)" value={mongoURI} onChange={(e) => setMongoURI(e.target.value)} className={inputClass} />
              <input type="text" placeholder="MongoDB Database Name (optional)" value={mongoDBName} onChange={(e) => setMongoDBName(e.target.value)} className={inputClass} />
            </div>
          )}

          {/* SUBMIT BUTTON */}
          <div className="flex justify-center">
            <button
              onClick={handleUpload}
              disabled={loading}
              className="mt-6 bg-primary text-white hover:bg-primary-soft px-6 py-3 rounded-md text-lg font-extrabold shadow-md transition duration-300 disabled:opacity-70"
            >
              {loading ? "Processing..." : "Ingest"}
            </button>
          </div>
        </div>
      </div>

      <Footer />

      {/* LOADING OVERLAY */}
      {loading && (
        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center z-50">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-white mb-4" />
          <p className="text-lg font-semibold text-white">
            Ingesting document... Please wait
          </p>
        </div>
      )}
    </div>
  );
}

export default IngestionPage;